import type { PermissionCode } from '@ooh/contracts';
import { useContextQuery } from './use-context-query';

/**
 * Returns true only when the caller holds a fully-wildcard scope row
 * (mediaType === null AND ownership === null) for `code` — mirrors the
 * server's `hasFullyWildcardRow` check (apps/api/src/media-units/
 * media-units.service.ts:153-154). A media-type- or ownership-scoped row for
 * the same code does NOT count, unlike `useHasPermission` (flat presence) or
 * `useCanOnUnit` (matches a scoped row against one unit's context).
 *
 * Use this for a permission whose spec explicitly says a scoped grant is
 * equivalent to no grant at all (e.g. cost.view_rent — see
 * specs/field-visibility/spec.md's cost-gating requirement) — never for a
 * permission meant to be legitimately scopable per media type/ownership
 * (e.g. cost.view itself), which useHasPermission/useCanOnUnit still serve
 * correctly.
 *
 * Returns true (fail-open) while context is loading — the server is the
 * authoritative enforcement point, matching useHasPermission/useCanOnUnit's
 * own loading default (never useHasNarrowedVisibility's fail-closed default,
 * which exists only to suppress a secondary UI caveat, not to gate an action).
 */
export function useHasWildcardPermission(code: PermissionCode): boolean {
  const { data } = useContextQuery();
  if (data == null) return true;
  return (data.permissionScopes ?? []).some(
    (entry) => entry.code === code && entry.mediaType === null && entry.ownership === null,
  );
}
